import DOMPurify from 'dompurify';
import { IAsset, ITemplate, ITemplateField } from '../../types/damTypes';
import {
  A4_HEIGHT_MM,
  A4_WIDTH_MM,
  IMultiProductLayout,
  IPdfElement,
  IPdfTemplate,
  IProductSlot,
  LayoutPreset,
  LAYOUT_PRESETS,
  TemplateMode,
} from '../../types/pdfTemplateTypes';

export interface TemplateValidationIssue {
  level: 'error' | 'warning';
  code: string;
  message: string;
}

export type TemplateCoordinateSpace = 'workspace' | 'persisted';

const RICH_TEXT_PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}|\{([a-zA-Z0-9_.-]+)\}/g;
const SIMPLE_CSS_RULE_PATTERN = /([^{}]+)\{([^{}]+)\}/g;
const RICH_TEXT_FIELD_ALIASES: Record<string, string> = {
  produktnamn: 'title',
  produkttitel: 'title',
  namn: 'title',
  beskrivning: 'description',
  filnamn: 'fileName',
};

function escapeAttribute(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function normalizeCssDeclarations(rawStyle: string): string {
  const declarations = rawStyle
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
  const byProperty = new Map<string, string>();
  declarations.forEach((declaration) => {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex <= 0) return;
    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim();
    if (!property || !value) return;
    byProperty.set(property, value);
  });
  return Array.from(byProperty.entries())
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ');
}

function inlineSimpleRichTextStyles(rawHtml: string): string {
  const classRules = new Map<string, string[]>();
  const tagRules = new Map<string, string[]>();
  const tagClassRules = new Map<string, string[]>();

  const registerRule = (selector: string, declarations: string) => {
    if (!selector || !declarations) return;
    if (selector.includes(':') || selector.includes(' ') || selector.includes('>') || selector.includes('#') || selector.includes('[')) {
      return;
    }

    const classMatch = selector.match(/^\.(?<className>[a-zA-Z0-9_-]+)$/);
    if (classMatch?.groups?.className) {
      const next = classRules.get(classMatch.groups.className) || [];
      next.push(declarations);
      classRules.set(classMatch.groups.className, next);
      return;
    }

    const tagClassMatch = selector.match(/^(?<tag>[a-zA-Z][a-zA-Z0-9-]*)\.(?<className>[a-zA-Z0-9_-]+)$/);
    if (tagClassMatch?.groups?.tag && tagClassMatch.groups.className) {
      const key = `${tagClassMatch.groups.tag.toLowerCase()}.${tagClassMatch.groups.className}`;
      const next = tagClassRules.get(key) || [];
      next.push(declarations);
      tagClassRules.set(key, next);
      return;
    }

    const tagMatch = selector.match(/^(?<tag>[a-zA-Z][a-zA-Z0-9-]*)$/);
    if (tagMatch?.groups?.tag) {
      const key = tagMatch.groups.tag.toLowerCase();
      const next = tagRules.get(key) || [];
      next.push(declarations);
      tagRules.set(key, next);
    }
  };

  let html = rawHtml.replace(/<link\b[^>]*>/gi, '');
  html = html.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_match, cssText: string) => {
    const withoutComments = cssText.replace(/\/\*[\s\S]*?\*\//g, ' ');
    const rulePattern = new RegExp(SIMPLE_CSS_RULE_PATTERN.source, 'g');
    let ruleMatch: RegExpExecArray | null = rulePattern.exec(withoutComments);
    while (ruleMatch) {
      const selectorGroup = String(ruleMatch[1] || '').trim();
      const declarationGroup = normalizeCssDeclarations(String(ruleMatch[2] || ''));
      if (selectorGroup && declarationGroup) {
        selectorGroup
          .split(',')
          .map((selector) => selector.trim())
          .filter(Boolean)
          .forEach((selector) => registerRule(selector, declarationGroup));
      }
      ruleMatch = rulePattern.exec(withoutComments);
    }
    return '';
  });

  return html.replace(/<([a-zA-Z][\w-]*)([^>]*)>/g, (match, tagName: string, rawAttributes: string) => {
    const existingStyleMatch = rawAttributes.match(/\sstyle=(['"])([\s\S]*?)\1/i);
    const classMatch = rawAttributes.match(/\sclass=(['"])([\s\S]*?)\1/i);
    const classNames = classMatch?.[2]
      ? classMatch[2].split(/\s+/).map((className) => className.trim()).filter(Boolean)
      : [];

    const styleParts = [
      ...(tagRules.get(tagName.toLowerCase()) || []),
      ...classNames.flatMap((className) => classRules.get(className) || []),
      ...classNames.flatMap((className) => tagClassRules.get(`${tagName.toLowerCase()}.${className}`) || []),
      existingStyleMatch?.[2] || '',
    ].filter(Boolean);

    if (styleParts.length === 0) return match;

    const mergedStyle = normalizeCssDeclarations(styleParts.join('; '));
    if (!mergedStyle) return match;

    const cleanedAttributes = existingStyleMatch
      ? rawAttributes.replace(existingStyleMatch[0], '')
      : rawAttributes;

    return `<${tagName}${cleanedAttributes} style="${escapeAttribute(mergedStyle)}">`;
  });
}

function hasHtmlTags(value: string): boolean {
  return /<\s*\/?\s*[a-z][^>]*>/i.test(value);
}

export function getPageDimensionsMm(orientation: 'portrait' | 'landscape') {
  const isLandscape = orientation === 'landscape';
  return {
    widthMm: isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM,
    heightMm: isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM,
  };
}

export function getContentBoxMm(page: IPdfTemplate['page']) {
  const { widthMm: pageWidthMm, heightMm: pageHeightMm } = getPageDimensionsMm(page.orientation);
  const marginTop = page.marginMm?.top || 0;
  const marginRight = page.marginMm?.right || 0;
  const marginBottom = page.marginMm?.bottom || 0;
  const marginLeft = page.marginMm?.left || 0;

  return {
    xMm: marginLeft,
    yMm: marginTop,
    widthMm: Math.max(1, pageWidthMm - marginLeft - marginRight),
    heightMm: Math.max(1, pageHeightMm - marginTop - marginBottom),
  };
}

export function buildSlotsFromLayout(page: IPdfTemplate['page'], layout: IMultiProductLayout): IProductSlot[] {
  const contentBox = getContentBoxMm(page);

  const rows = Math.max(1, layout.rows || 1);
  const cols = Math.max(1, layout.cols || 1);
  const gapMm = Math.max(0, layout.gapMm ?? 5);

  const usableW = contentBox.widthMm;
  const usableH = contentBox.heightMm;
  const slotW = Math.max(5, (usableW - gapMm * (cols - 1)) / cols);
  const slotH = Math.max(5, (usableH - gapMm * (rows - 1)) / rows);

  const slots: IProductSlot[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      slots.push({
        id: `auto-${r}-${c}`,
        xMm: c * (slotW + gapMm),
        yMm: r * (slotH + gapMm),
        wMm: slotW,
        hMm: slotH,
      });
    }
  }
  return slots;
}

const roundMm = (value: number) => Math.round(value * 1000) / 1000;

const toWorkspaceX = (value: number, offset: number) => roundMm(Number(value ?? 0) - offset);
const toPersistedX = (value: number, offset: number) => roundMm(Number(value ?? 0) + offset);

export function normalizeTemplate(
  template: Partial<IPdfTemplate>,
  defaults: Omit<IPdfTemplate, '_id'>,
  defaultPage: IPdfTemplate['page'],
  coordinateSpace: TemplateCoordinateSpace = 'workspace'
): IPdfTemplate {
  const normalized: IPdfTemplate = {
    ...defaults,
    ...template,
    page: {
      ...defaultPage,
      ...(template.page || {}),
      marginMm: {
        ...defaultPage.marginMm,
        ...(template.page?.marginMm || {}),
      },
    },
    elements: (template.elements || []).map((el) => ({
      ...el,
      style: el.style || {},
      zIndex: el.zIndex ?? 0,
      locked: el.locked ?? false,
    })),
    layout: template.layout ? { ...template.layout } : undefined,
    productSlots: (template.productSlots || []).map((slot) => ({
      id: slot.id || `slot-${Math.random().toString(36).slice(2, 8)}`,
      blockTemplateId: slot.blockTemplateId,
      xMm: Number(slot.xMm ?? 0),
      yMm: Number(slot.yMm ?? 0),
      wMm: Number(slot.wMm ?? 10),
      hMm: Number(slot.hMm ?? 10),
    })),
  } as IPdfTemplate;

  const mode = (normalized.templateMode || 'single') as TemplateMode;
  normalized.templateMode = mode;

  if (mode === 'multi') {
    const preset = (normalized.layout?.preset || '3x2') as LayoutPreset;
    const presetCfg = LAYOUT_PRESETS[preset] || LAYOUT_PRESETS['3x2'];
    normalized.layout = {
      preset,
      rows: normalized.layout?.rows || presetCfg.rows,
      cols: normalized.layout?.cols || presetCfg.cols,
      gapMm: normalized.layout?.gapMm ?? 5,
    };
  }

  if (coordinateSpace === 'persisted') {
    const offsetX = normalized.page.marginMm?.left || 0;
    const offsetY = normalized.page.marginMm?.top || 0;
    normalized.elements = normalized.elements.map((element) => ({
      ...element,
      xMm: toWorkspaceX(element.xMm, offsetX),
      yMm: toWorkspaceX(element.yMm, offsetY),
    }));
    normalized.productSlots = (normalized.productSlots || []).map((slot) => ({
      ...slot,
      xMm: toWorkspaceX(slot.xMm, offsetX),
      yMm: toWorkspaceX(slot.yMm, offsetY),
    }));
  }

  return normalized;
}

export function serializeTemplate(template: IPdfTemplate): IPdfTemplate {
  const offsetX = template.page.marginMm?.left || 0;
  const offsetY = template.page.marginMm?.top || 0;

  return {
    ...template,
    page: {
      ...template.page,
      marginMm: {
        ...template.page.marginMm,
      },
    },
    elements: (template.elements || []).map((element) => ({
      ...element,
      style: { ...(element.style || {}) },
      binding: element.binding ? { ...element.binding } : undefined,
      xMm: toPersistedX(element.xMm, offsetX),
      yMm: toPersistedX(element.yMm, offsetY),
    })),
    layout: template.layout ? { ...template.layout } : undefined,
    productSlots: (template.productSlots || []).map((slot) => ({
      ...slot,
      xMm: toPersistedX(slot.xMm, offsetX),
      yMm: toPersistedX(slot.yMm, offsetY),
    })),
  };
}

export function getAssetMetadataValue(asset: IAsset | null, key: string, fallback?: string): string {
  if (!asset) return fallback || '';

  const normalizedKey = String(key || '').trim();
  const lowerKey = normalizedKey.toLowerCase();
  const candidateKeys = Array.from(new Set([
    normalizedKey,
    lowerKey,
    RICH_TEXT_FIELD_ALIASES[lowerKey],
  ].filter(Boolean))) as string[];

  for (const candidateKey of candidateKeys) {
    const candidateLower = candidateKey.toLowerCase();
    if (candidateLower === 'title') return asset.title || fallback || '';
    if (candidateLower === 'description') return asset.description || fallback || '';
    if (candidateLower === 'filename') return asset.fileName || fallback || '';
    if (candidateLower === 'filetype') return asset.fileType || fallback || '';
    if (candidateLower === 'tags') return (asset.tags || []).join(', ') || fallback || '';
    if (candidateLower === 'aidescription') return asset.aiDescription || fallback || '';
    if (candidateLower === 'aitags') return (asset.aiTags || []).join(', ') || fallback || '';
  }

  const metadata = (asset as any).metadata;
  if (metadata instanceof Map) {
    for (const candidateKey of candidateKeys) {
      const value = metadata.get(candidateKey);
      if (value !== undefined && value !== null) return String(value);
    }
    let matchedValue: unknown;
    metadata.forEach((entryValue, entryKey) => {
      if (candidateKeys.some((candidateKey) => String(entryKey).toLowerCase() === candidateKey.toLowerCase())) {
        matchedValue = entryValue;
      }
    });
    if (matchedValue !== undefined && matchedValue !== null) return String(matchedValue);
  } else if (metadata && typeof metadata === 'object') {
    for (const candidateKey of candidateKeys) {
      const value = metadata[candidateKey];
      if (value !== undefined && value !== null) return String(value);
    }
    const matchedEntry = Object.entries(metadata).find(([entryKey]) => (
      candidateKeys.some((candidateKey) => entryKey.toLowerCase() === candidateKey.toLowerCase())
    ));
    if (matchedEntry && matchedEntry[1] !== undefined && matchedEntry[1] !== null) {
      return String(matchedEntry[1]);
    }
  }

  return fallback || '';
}

export function escapeHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function containsRichTextPlaceholders(raw?: string): boolean {
  if (!raw) return false;
  return /\{\{\s*[a-zA-Z0-9_.-]+\s*\}\}|\{[a-zA-Z0-9_.-]+\}/.test(raw);
}

export function renderRichTextHtml(raw: string, asset: IAsset | null): string {
  if (!raw) return '';
  let html = inlineSimpleRichTextStyles(String(raw).replace(/\r\n/g, '\n'));
  html = html.replace(RICH_TEXT_PLACEHOLDER_PATTERN, (_match, doubleKey: string, singleKey: string) => {
    const key = String(doubleKey || singleKey || '').trim();
    const value = getAssetMetadataValue(asset, key, `{${key}}`);
    return escapeHtml(String(value ?? ''));
  });
  if (!hasHtmlTags(html)) {
    html = html.replace(/\n/g, '<br/>');
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['span', 'br', 'div', 'p', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'sup', 'sub', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'small', 'section', 'article', 'header', 'footer', 'hr'],
    ALLOWED_ATTR: ['style'],
  });
}

export function isDynamicPageElementInMulti(element: IPdfElement): boolean {
  if (element.kind === 'productImage') return true;
  if (element.kind === 'textField') return Boolean(element.binding?.metadataKey);
  if (element.kind === 'richText') return containsRichTextPlaceholders(element.content);
  return false;
}

export function buildAvailableFields(fieldTemplates: ITemplate[], templateType: IPdfTemplate['type']) {
  const categoryName = templateType === 'products' ? 'Produkter' : 'Vin';
  const matchingTemplates = fieldTemplates.filter((template) => {
    const name = (template.name || '').toLowerCase();
    return name.includes(categoryName.toLowerCase()) || name.includes(templateType);
  });
  const sourceTemplates = matchingTemplates.length > 0 ? matchingTemplates : fieldTemplates;

  const fields = new Map<string, { id: string; label: string; templateName: string }>();
  const registerField = (id: string, label: string, templateName: string) => {
    if (!id || fields.has(id)) return;
    fields.set(id, { id, label, templateName });
  };

  registerField('title', 'Titel', 'Standard');
  registerField('description', 'Beskrivning', 'Standard');
  registerField('fileName', 'Filnamn', 'Standard');
  registerField('tags', 'Taggar', 'Standard');
  registerField('aiDescription', 'AI-beskrivning', 'Standard');
  registerField('aiTags', 'AI-taggar', 'Standard');

  sourceTemplates.forEach((template) => {
    (template.fields || []).forEach((field: ITemplateField) => {
      if (field.type === 'separator') return;
      const id = field.id || field.name;
      if (!id) return;
      registerField(id, field.label || field.name || id, template.name || 'Mall');
    });
  });

  return Array.from(fields.values());
}

export function getMultiPageValidationIssues(template: IPdfTemplate): TemplateValidationIssue[] {
  const issues: TemplateValidationIssue[] = [];
  if (template.templateMode !== 'multi') return issues;
  const contentBox = getContentBoxMm(template.page);

  const slots = (template.productSlots && template.productSlots.length > 0)
    ? template.productSlots
    : template.layout
      ? buildSlotsFromLayout(template.page, template.layout)
      : [];

  if (slots.length === 0) {
    issues.push({
      level: 'error',
      code: 'multi-no-slots',
      message: 'Multi-mallen saknar produktplatser.',
    });
  }

  slots.forEach((slot, index) => {
    if ((slot.wMm ?? 0) <= 0 || (slot.hMm ?? 0) <= 0) {
      issues.push({
        level: 'error',
        code: 'slot-invalid-size',
        message: `Produktplats ${index + 1} har ogiltiga mått.`,
      });
    }
    if (
      (slot.xMm ?? 0) < 0 ||
      (slot.yMm ?? 0) < 0 ||
      (slot.xMm ?? 0) + (slot.wMm ?? 0) > contentBox.widthMm + 0.001 ||
      (slot.yMm ?? 0) + (slot.hMm ?? 0) > contentBox.heightMm + 0.001
    ) {
      issues.push({
        level: 'error',
        code: 'slot-outside-page',
        message: `Produktplats ${index + 1} ligger utanför sidans innehållsyta.`,
      });
    }
    if (!slot.blockTemplateId) {
      issues.push({
        level: 'error',
        code: 'slot-missing-block',
        message: `Produktplats ${index + 1} saknar produktblock.`,
      });
    }
  });

  (template.elements || []).forEach((element) => {
    if (isDynamicPageElementInMulti(element)) {
      issues.push({
        level: 'warning',
        code: 'multi-dynamic-page-element',
        message: `Element "${element.id}" använder produktdata på sidnivå i multi-läge. Flytta produktdata till produktblock.`,
      });
    }
    if (element.kind === 'textField' && !element.binding?.metadataKey && !element.binding?.fallbackValue) {
      issues.push({
        level: 'warning',
        code: 'text-field-unbound',
        message: `Datafält "${element.id}" saknar databindning och standardvärde.`,
      });
    }
  });

  return issues;
}

export function getAssignedMultiSlotAsset(multiPreviewAssets: IAsset[], slotIdx: number): IAsset | null {
  return multiPreviewAssets[slotIdx] || null;
}

export function getTemplatePreviewKey(template: IPdfTemplate, previewAssetId: string, multiPreviewAssets: IAsset[]): string {
  return JSON.stringify({
    template,
    previewAssetId,
    multiPreviewAssetIds: multiPreviewAssets.map((asset) => asset._id),
  });
}
